import { useAsync } from "@/shared/hooks";

import { teamPicksLoader } from "../loader/team-picks-loader";
import { teams } from "@/shared/utils/teams";
import {
  ChevronsDownUp,
  ChevronsUpDown,
  CircleHelp,
  CornerDownRight,
  History,
} from "lucide-react";

import { flexRender, getExpandedRowModel } from "@tanstack/react-table";
import {
  Button,
  cn,
  Flex,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@suns/design-system";
import { useReactTable } from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import {
  formatMoney,
  formatRank,
  getValueColor,
} from "@/shared/utils/helper-functions";
import { TeamLogo } from "@/components/TeamLogo/TeamLogo";
import { LeagueId, SunsApiError } from "@suns/api";
import {
  PickAssetRow,
  PickRow,
  TeamRow,
} from "@suns/api/generated-client/apollo";
import { Pick, PickAssetChart } from "./PickAssetChart";
import React from "react";

export function TeamPicks({ team }: { team: TeamRow }) {
  const {
    response: pickAssets,
    loading,
    error,
  } = useAsync(teamPicksLoader, { teamId: team.id });

  if (error) {
    throw new SunsApiError("Error loading team picks", { cause: error });
  }

  return (
    <PickTable
      pickAssets={pickAssets ?? []}
      currentTeam={team}
      loading={loading}
    />
  );
}

const PickTable = ({
  pickAssets,
  currentTeam,
  loading,
}: {
  pickAssets: PickAssetRow[];
  currentTeam: TeamRow;
  loading: boolean;
}) => {
  const formatAssetMessage = (pickAsset: PickAssetRow) => {
    const assetTeam = pickAsset.picks.map((pick) =>
      teams.find((team) => team.id === pick.teamId)
    );
    switch (pickAsset.type.toUpperCase()) {
      case PickAssetRow.type.SWAP:
        return `${assetTeam[0]?.TEAM_ABBR} swap with ${assetTeam[1]?.TEAM_ABBR}`;
      case PickAssetRow.type.FAVORABLE:
        if (pickAsset.priority === 1) {
          return "Most favorable";
        } else if (pickAsset.priority === pickAsset.picks.length) {
          return "Least favorable";
        } else {
          return `${formatRank(pickAsset.priority)} most favorable`;
        }
      default:
        return "--";
    }
  };

  const table = useReactTable({
    data: pickAssets,
    columns: [
      {
        header: " ",
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: "pointer" }}
            >
              {row.getIsExpanded() ? (
                <div className="mx-3 rounded-full bg-primary p-1">
                  <ChevronsDownUp className="h-4 w-4 text-white" />
                </div>
              ) : (
                <div className="mx-3 rounded-full bg-primary p-1">
                  <ChevronsUpDown className="h-4 w-4 text-white" />
                </div>
              )}
            </button>
          ) : (
            ""
          );
        },
      },
      {
        header: "Year",
        accessorKey: "year",
      },
      {
        header: "Round",
        accessorKey: "round",
      },
      {
        header: "Asset",
        accessorKey: "asset",
        cell: ({ row }) => {
          return formatAssetMessage(row.original);
        },
      },
      {
        header: "Value",
        accessorKey: "value",
        cell: ({ row }) => {
          const totalPickValue = row.original.picks
            .flatMap((pick) =>
              pick.pickSplits.flatMap(
                (split) =>
                  split.pickOutcomes.find(
                    (outcome) => outcome.teamId === currentTeam.id
                  )?.value || 0
              )
            )
            .reduce((sum, value) => sum + value, 0);
          return totalPickValue > 0 ? formatMoney(totalPickValue) : "--";
        },
      },
      {
        header: "Proj.pick",
        accessorKey: "Pick Projection",
        cell: ({ row }) => {
          const pickProjectionTeam = teams.find(
            (team) => team.id === row.original.picks[0].teamId
          );

          return pickProjectionTeam?.TEAM_ABBR ?? "--";
        },
      },
      {
        header: "Proj.#",
        accessorKey: "projectedPickNumber",
        cell: ({ row }) => {
          return row.original.picks[0]?.projectedPickNumber ?? "--";
        },
      },
      {
        header: "Chance",
        accessorKey: "chance",
        cell: ({ row }) => {
          const chance = row.original.picks.map(
            (pick) =>
              pick.pickSplits[0]?.pickOutcomes.find(
                (outcome) => outcome.teamId === currentTeam.id
              )?.chance
          );
          return (
            <Text
              className={cn(
                Number(chance[0]) < 50 ? "text-red-600" : "text-green-600"
              )}
              size="sm"
              heading
            >
              {chance[0] ? `${chance[0]}%` : "--"}
            </Text>
          );
        },
      },
    ],
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => true,
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      expanded: true,
    },
  });

  if (loading) {
    return (
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    <Text size="sm" heading>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Text>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getHeaderGroups().map((headerGroup) =>
            [1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell key={header.id}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  }

  if (!pickAssets?.length)
    return (
      <Text muted size="lg" className="py-4 text-center">
        No picks found.
      </Text>
    );

  return (
    <Flex direction="down" gap="md">
      <Flex className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      <Text size="sm" heading>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Text>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              return (
                <React.Fragment key={row.id}>
                  <TableRow>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className="whitespace-nowrap p-1"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={row.getAllCells().length}>
                        <Flex direction="down" gap="md">
                          <DesktopPickView
                            pickAsset={row.original}
                            currentTeam={currentTeam}
                          />
                          <MobilePickView
                            pickAsset={row.original}
                            currentTeam={currentTeam}
                          />

                          <Flex direction="right" gap="sm" className="pl-4">
                            {row.original.contingentOn && (
                              <Flex direction="right" gap="sm" align="center">
                                <CircleHelp className="h-4 w-4 text-yellow-600" />
                                <Text size="xs">
                                  {row.original.contingentOn}
                                </Text>
                              </Flex>
                            )}
                            {row.original.fallbackOn && (
                              <Flex direction="right" gap="sm" align="center">
                                <CornerDownRight className="h-4 w-4 text-yellow-600" />
                                <Text size="xs">{row.original.fallbackOn}</Text>
                              </Flex>
                            )}
                          </Flex>
                        </Flex>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Flex>
    </Flex>
  );
};

interface PickViewProps {
  pickAsset?: PickAssetRow;
  currentTeam: TeamRow;
}

const pickOutcomeChance = (pick: PickRow, currentTeamId: number) => {
  return pick.pickSplits
    .map((split) =>
      split.pickOutcomes.find((outcome) => outcome.teamId === currentTeamId)
    )
    .map((outcome) => outcome?.chance)
    .filter(
      (chance: number | null | undefined) =>
        chance !== null && chance !== undefined
    )[0];
};

const compactCurrency = (pick: PickRow) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    compactDisplay: "short",
  }).format(pick.value || 0);
};

function DesktopPickView({ pickAsset, currentTeam }: PickViewProps) {
  return (
    <Flex className="max-xl:hidden" direction="right" gap="md">
      <Flex className="w-[50%]" direction="down" gap="md">
        <Grid columns={"4"} justify="center" align="center" className="">
          <Text size="sm" heading muted className="whitespace-nowrap">
            Teams Pick
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            Projected
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            {currentTeam.code} Chance
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            {currentTeam.code} Value
          </Text>
        </Grid>
        {pickAsset?.picks.map((pick, index) => {
          return (
            <Grid key={index} columns={"4"} justify="center" align="center">
              <TeamLogo
                nbaTeamId={teams.find((t) => t.id === pick.teamId)?.TEAM_ID}
                leagueId={LeagueId.NBA}
              />
              <Text size="2xl" className="font-semibold text-gray-600">
                #{pick.projectedPickNumber}
              </Text>
              <Text
                size="2xl"
                heading
                className={cn(
                  "font-semibold",
                  getValueColor(Number(pickOutcomeChance(pick, currentTeam.id)))
                )}
              >
                {Number(pickOutcomeChance(pick, currentTeam.id))}%
              </Text>
              <Text size="2xl" heading className="font-semibold text-gray-600">
                {compactCurrency(pick)}
              </Text>
            </Grid>
          );
        })}
      </Flex>
      <Flex className="w-full" direction="down" gap="md" justify="start">
        <Text size="sm" heading muted className="mb-1 text-center">
          Breakdown
        </Text>
        {pickAsset && <PickAssetChart pickAsset={pickAsset} />}
      </Flex>
      <Flex direction="down" gap="lg" align="center" justify="start">
        <Text size="sm" heading muted>
          Timeline
        </Text>
        {pickAsset?.picks.map((_, index) => {
          return (
            <Button
              key={index}
              variant="ghost"
              size="sm"
              className={cn(index !== 0 && "mt-3")}
            >
              <History key={index} className={"h-8 w-8 text-primary"} />
            </Button>
          );
        })}
      </Flex>
    </Flex>
  );
}

function MobilePickView({ pickAsset, currentTeam }: PickViewProps) {
  return (
    <Flex
      className="w-full xl:hidden"
      direction="down"
      gap="md"
      justify="start"
    >
      {pickAsset?.picks.map((pick) => (
        <Flex key={pick.id} direction="down" gap="md">
          <Flex className="sticky left-0 w-2/3" direction="down" gap="md">
            <Grid columns={"5"} justify="center" align="center">
              <Text size="sm" heading muted className="whitespace-nowrap">
                Teams Pick
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                Projected
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                {currentTeam.code} Chance
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                {currentTeam.code} Value
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                Timeline
              </Text>
            </Grid>
            <Grid columns={"5"} justify="center" align="center">
              <TeamLogo
                nbaTeamId={teams.find((t) => t.id === pick.teamId)?.TEAM_ID}
                leagueId={LeagueId.NBA}
              />
              <Text size="2xl" className="font-semibold text-gray-600">
                #{pick.projectedPickNumber}
              </Text>
              <Text
                size="2xl"
                heading
                className={cn(
                  "font-semibold",
                  getValueColor(Number(pickOutcomeChance(pick, currentTeam.id)))
                )}
              >
                {pickOutcomeChance(pick, currentTeam.id)}%
              </Text>
              <Text size="2xl" heading className="font-semibold text-gray-600">
                {compactCurrency(pick)}
              </Text>
              <Button variant="ghost" size="sm">
                <History className={"h-8 w-8 text-primary"} />
              </Button>
            </Grid>
          </Flex>
          <Pick pick={pick} />
        </Flex>
      ))}
    </Flex>
  );
}
